import { supabase } from "@/integrations/supabase/client";

export interface BroadcastMessage {
  id: string;
  message: string;
  created_at: string;
  sent_by?: string | null;
}

export async function checkIsAdmin(userId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_admin", { user_id: userId });

  if (error) {
    console.error("[broadcastService] is_admin error:", error);
    return false;
  }

  return Boolean(data);
}

export async function sendBroadcastMessage(
  message: string,
  sentBy: string
): Promise<{ data: BroadcastMessage | null; error: Error | null }> {
  const trimmed = message.trim();
  if (!trimmed) {
    return { data: null, error: new Error("Le message ne peut pas être vide.") };
  }

  const isAdmin = await checkIsAdmin(sentBy);
  if (!isAdmin) {
    return { data: null, error: new Error("Permissions administrateur requises.") };
  }

  const { data, error } = await supabase
    .from("broadcast_messages")
    .insert([{ message: trimmed, sent_by: sentBy }])
    .select("id, message, created_at, sent_by")
    .single();

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as BroadcastMessage, error: null };
}

export async function fetchBroadcastMessages(): Promise<{
  data: BroadcastMessage[];
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from("broadcast_messages")
    .select("id, message, created_at, sent_by")
    .order("created_at", { ascending: false });

  if (error) {
    return { data: [], error: new Error(error.message) };
  }

  return { data: (data ?? []) as BroadcastMessage[], error: null };
}

export async function fetchReadBroadcastIds(userId: string): Promise<{
  data: string[];
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from("broadcast_reads")
    .select("broadcast_id")
    .eq("user_id", userId);

  if (error) {
    return { data: [], error: new Error(error.message) };
  }

  return { data: (data ?? []).map((row) => row.broadcast_id), error: null };
}

export async function markBroadcastsAsRead(
  userId: string,
  broadcastIds: string[]
): Promise<{ error: Error | null }> {
  if (broadcastIds.length === 0) {
    return { error: null };
  }

  const rows = broadcastIds.map((broadcastId) => ({
    user_id: userId,
    broadcast_id: broadcastId,
  }));

  const { error } = await supabase
    .from("broadcast_reads")
    .upsert(rows, { onConflict: "user_id,broadcast_id", ignoreDuplicates: true });

  if (error) {
    return { error: new Error(error.message) };
  }

  return { error: null };
}
